'use client';

import { useEffect, useRef, useState } from 'react';

const COOKIE = 'adipa-pass-code';
const SYNC_EVENT = 'adipa:pass-code-ticket';
const APPLY_EVENT = 'adipa:pass-code-apply';
const LEAVE_MS = 240;
const DESKTOP_QUERY = '(min-width: 1024px)';

const SURFACES: Record<string, string> = {
  acreditaciones: 'linear-gradient(115deg,#0272aa,#2cb7ff 78%,#5cccff)',
  asincronicos: 'linear-gradient(115deg,#005a0b,#00770e 62%,#2aa83a)',
  diplomados: 'linear-gradient(115deg,#43289c,#704efd 62%,#5c7dfb)',
  especializaciones: 'linear-gradient(115deg,#a3004f,#ff017c 72%,#ff5ba6)',
  magistrales: 'linear-gradient(115deg,#8f4a00,#cc6a00 66%,#f08a1e)',
  sincronicos: 'linear-gradient(115deg,#5c7dfb,#704efd 66%,#43289c)',
};

const FALLBACK_SURFACE = 'linear-gradient(115deg,#43289c,#704efd 62%,#2cb7ff)';

interface PassCodeTicket {
  code: string;
  label: string;
  key: string;
}

const readTicket = (): PassCodeTicket | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]*)`));
  if (!match?.[1]) return null;

  const [code, label, key] = decodeURIComponent(match[1]).split('|');

  return code ? { code, key: key ?? '', label: label || code } : null;
};

const findHeader = (anchor: HTMLElement | null): HTMLElement | null => {
  const root = anchor?.getRootNode();
  const scope = root instanceof ShadowRoot || root instanceof Document ? root : document;

  return scope.querySelector('header');
};

const clearTicket = () => {
  document.cookie = `${COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  window.dispatchEvent(new Event(SYNC_EVENT));
};

/**
 * El código del Pase Anual que la persona copió en su panel queda colgando del
 * navbar hasta que lo usa. Vive acá para que el front nuevo y la tienda legacy
 * lo muestren igual: ambos montan este widget y leen la misma cookie. Al
 * tocarlo emite `adipa:pass-code-apply`, y cada sitio decide qué hacer.
 */
export const PassCodeRail = () => {
  const [ticket, setTicket] = useState<PassCodeTicket | null>(null);
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const sync = () => {
      setTicket(readTicket());
      setLeaving(false);
      setOpen(false);
    };

    sync();
    window.addEventListener(SYNC_EVENT, sync);
    window.addEventListener('focus', sync);

    const header = findHeader(anchorRef.current);
    const place = () => {
      const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
      document.documentElement.style.setProperty(
        '--adipa-pass-rail-top',
        `${Math.max(bottom, 0)}px`,
      );
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, { passive: true });

    const media = window.matchMedia(DESKTOP_QUERY);
    const match = () => setIsDesktop(media.matches);
    match();
    media.addEventListener('change', match);

    return () => {
      media.removeEventListener('change', match);
      window.removeEventListener(SYNC_EVENT, sync);
      window.removeEventListener('focus', sync);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place);
    };
  }, []);

  const anchor = <span hidden ref={anchorRef} />;

  if (!ticket) return anchor;

  const background = SURFACES[ticket.key] ?? FALLBACK_SURFACE;
  const expanded = isDesktop || open;

  const remove = () => {
    setLeaving(true);
    window.setTimeout(clearTicket, LEAVE_MS);
  };

  const apply = () => {
    window.dispatchEvent(new CustomEvent(APPLY_EVENT, { detail: { ...ticket } }));
  };

  return (
    <>
      {anchor}
      <div className="adipa-pass-rail pointer-events-none fixed inset-x-0 z-40 flex justify-end px-4 sm:px-6">
        <div
          className={[
            'adipa-pass-rail__card rounded-b-adipa-md pointer-events-auto flex items-stretch overflow-hidden text-white shadow-lg ring-1 ring-white/20',
            leaving ? 'adipa-pass-rail__card--leaving' : '',
            open && !isDesktop ? 'adipa-pass-rail__card--expand' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ background }}
        >
          {expanded ? (
            <button
              aria-label={`Usar el código ${ticket.code}`}
              className="flex cursor-pointer flex-col justify-center gap-0.5 py-2 pl-4 pr-3 text-left"
              onClick={apply}
              type="button"
            >
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/70">
                {ticket.label}
              </span>
              <span className="text-[14px] font-bold leading-none tracking-wide">
                {ticket.code}
              </span>
            </button>
          ) : (
            <button
              aria-label={`Ver el código ${ticket.code}`}
              className="flex size-9 cursor-pointer items-center justify-center"
              onClick={() => setOpen(true)}
              type="button"
            >
              <svg aria-hidden className="size-[18px]" fill="currentColor" viewBox="0 0 256 256">
                <path d="M232 104a8 8 0 0 0 8-8V64a16 16 0 0 0-16-16H32a16 16 0 0 0-16 16v32a8 8 0 0 0 8 8 24 24 0 0 1 0 48 8 8 0 0 0-8 8v32a16 16 0 0 0 16 16h192a16 16 0 0 0 16-16v-32a8 8 0 0 0-8-8 24 24 0 0 1 0-48Z" />
              </svg>
            </button>
          )}

          <button
            aria-label="Quitar el código guardado"
            className="flex w-8 cursor-pointer items-center justify-center border-l border-dashed border-white/40 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            onClick={remove}
            type="button"
          >
            <svg aria-hidden className="size-3.5" fill="currentColor" viewBox="0 0 256 256">
              <path d="M205.66 194.34a8 8 0 0 1-11.32 11.32L128 139.31l-66.34 66.35a8 8 0 0 1-11.32-11.32L116.69 128 50.34 61.66a8 8 0 0 1 11.32-11.32L128 116.69l66.34-66.35a8 8 0 0 1 11.32 11.32L139.31 128Z" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};
